import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    updateBankDetailsById,
    getBankDetailsById,
    deleteBankDetailsById,
    getAllBankDetailByUserId,
    openEditModal
} from '../../../../../app/store/slices/bank/bank';
import { errorToast } from '../../../../../utils/toasts/toasts';

export default function useAddedBankList() {
    const dispatch = useDispatch();
    const [deletingId, setDeletingId] = useState(null);
    const [editLoadingId, setEditLoadingId] = useState(null);
    const { bankRecord, loading } = useSelector((state) => state.bank);
    // console.log("Bank Record -> ", bankRecord?.data)
    const user = useSelector((state) => state.auth.user);

    const initialValues = {
        bankName: bankRecord?.data?.bank_name,
        accountHolderName: bankRecord?.data?.account_holder_name,
        accountNumber: bankRecord?.data?.account_number,
        confirmAccountNumber: bankRecord?.data?.account_number,
        ifscCode: bankRecord?.data?.ifsc_code,
        accountType: bankRecord?.data?.account_type,
        nickName: bankRecord?.data?.nickname,
        isTermsAccepted: bankRecord?.data?.tnc_accepted
    };

    const validationSchema = Yup.object({
        bankName: Yup.string().required('Bank Name is required'),
        accountHolderName: Yup.string().required('Account Holder Name is required'),
        accountNumber: Yup.string().required('Account Number is required'),
        confirmAccountNumber: Yup.string()
            .oneOf([Yup.ref('accountNumber'), null], 'Account numbers must match')
            .required('Confirm Account Number is required'),
        ifscCode: Yup.string().required('IFSC Code is required'),
        accountType: Yup.string().required('Account Type is required'),
        nickName: Yup.string().required('Nick Name is required'),
        isTermsAccepted: Yup.boolean().oneOf([true], 'You must accept the terms and conditions')
    });

    const formik = useFormik({
        initialValues,
        validationSchema,
        enableReinitialize: true,
        onSubmit: async (values, { resetForm, setFieldError, setSubmitting }) => {
            try {
                const payload = {
                    id: bankRecord?.data?.id,
                    user_id: user?.id,
                    bank_name: values.bankName,
                    account_holder_name: values.accountHolderName,
                    account_number: values.accountNumber,
                    ifsc_code: values.ifscCode,
                    account_type: values.accountType,
                    is_primary: bankRecord?.data?.is_primary || false,
                    nickname: values.nickName,
                    tnc_accepted: values.isTermsAccepted
                };

                await dispatch(
                    updateBankDetailsById({
                        id: bankRecord?.data?.id,
                        data: payload
                    })
                ).unwrap();

                resetForm();
            } catch (error) {
                // console.error("Error updating bank details:", error);
                if (error?.status_code === 422) {
                    if (error.message?.includes('Nickname already exists')) {
                        setFieldError(
                            'nickName',
                            'This nickname is already in use. Please choose another one.'
                        );
                    }
                }
                setSubmitting(false);
            }
        }
    });

    /**
     * TODO: fix this to avoid infinite loop.
     */
    // useEffect(() => {
    //     if (user?.id) {
    //         dispatch(getAllBankDetailByUserId({ user_id: user.id }));
    //     }
    // }, [user?.id, dispatch]);

    async function handleEdit(id) {
        setEditLoadingId(id);
        try {
            await dispatch(getBankDetailsById({ id })).unwrap();
            dispatch(openEditModal(true));
        } catch (error) {
            console.error('Failed to fetch bank details', error);
        } finally {
            setEditLoadingId(null);
        }
    }

    async function deleteBankDetail(id) {
        setDeletingId(id);
        try {
            await dispatch(deleteBankDetailsById({ id })).unwrap();
        } catch (error) {
            console.error('Failed to delete bank details', error);
            return null;
        } finally {
            setDeletingId(null);
        }
    }

    function closeEditModal() {
        dispatch(openEditModal(false));
        formik.resetForm();
    }


    async function handleMarkAsPrimary(id) {
        try {
            setEditLoadingId(id);
            const payload = {
                id: id,
                data: {
                    is_primary: true
                }
            };
            await dispatch(updateBankDetailsById(payload)).unwrap();

            // Refetch all bank details to update the list
            if (user?.id) {
                await dispatch(getAllBankDetailByUserId({ user_id: user.id })).unwrap();
            }

            setEditLoadingId(null);
        } catch (error) {
            errorToast("Failed to mark as primary account. Please try again.");
        }
    }


    return {
        deleteBankDetail,
        deletingId,
        formik,
        accountTypes,
        loading,
        handleEdit,
        editLoadingId,
        closeEditModal,
        handleMarkAsPrimary
    };
}

const accountTypes = [
    { label: 'Savings', value: 'savings' },
    { label: 'Current', value: 'current' }
];
